import { useCallback, useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { FlowGraph, FlowGraphNode } from '../api/contracts';
import { AutoRefreshToggle } from '../components/AutoRefreshToggle';
import { ErrorBanner } from '../components/ErrorBanner';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { FlowGraphCanvas, FlowGraphLegend } from '../components/FlowGraphCanvas';
import { FlowStepDetail } from '../components/FlowStepDetail';
import { RunTimeline } from '../components/RunTimeline';
import {
  ProcessReferencePanel,
  ProcessReferenceToggle,
  useProcessReferencePanel,
} from '../components/ProcessReferencePanel';
import type { ProcessReferencePanelState } from '../components/ProcessReferencePanel';
import { useAutoRefreshPreference } from '../hooks/use-auto-refresh-preference';
import { useLiveFlowGraph } from '../hooks/use-live-flow-graph';
import { REFRESH_INTERVAL_MS } from '../lib/auto-refresh';
import { flowLabel } from '../lib/catalogs';
import { resolveStepTarget } from '../lib/flow-steps';
import { formatDateTime } from '../lib/format';
import { flowsOfRun } from '../lib/process-reference';

/**
 * La corrida como grafo, a pantalla completa.
 *
 * El mismo grafo se embebe en el detalle del evento, que es donde se mira la
 * mayoria de las veces: se llega a un mensaje y se quiere saber en que parte del
 * flujo cayo. Esta pantalla existe para el caso contrario —mirar una corrida sin
 * partir de ninguno de sus mensajes— y para poder enlazarla.
 */

export function FlowGraphPage(): ReactElement {
  const navigate = useNavigate();
  const { correlationId } = useParams<{ correlationId: string }>();
  const id = correlationId ?? '';
  // Misma preferencia que el detalle y la traza (`run`): son vistas de la misma corrida.
  const [autoRefresh, setAutoRefresh] = useAutoRefreshPreference('run');
  const resource = useLiveFlowGraph(id, { enabled: autoRefresh });
  const [selected, setSelected] = useState<FlowGraphNode | null>(null);

  const graph = resource.data;
  const selectedStep = graph?.nodes.find((step) => step.id === selected?.id) ?? null;

  const onSelectStep = useCallback(
    (step: FlowGraphNode) => {
      const target = resolveStepTarget(step);
      if (target.kind === 'message') {
        navigate(`/mensajes/${target.messageId}`);
        return;
      }
      setSelected(step);
    },
    [navigate],
  );

  // La guía de los flujos de la corrida, en un panel lateral que no saca del grafo.
  const reference = useProcessReferencePanel(flowsOfRun(graph));

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <p className="muted">
            <Link to="/corridas">← Corridas</Link>
            {' · '}
            <Link to="/mensajes">Mensajes</Link>
            {' · '}
            <Link to={`/traza/${id}`}>Ver como lista</Link>
          </p>
          <h1>Flujo de la corrida</h1>
          <p className="page__subtitle mono">{id}</p>
        </div>
        <div className="row">
          <AutoRefreshToggle
            enabled={autoRefresh}
            onChange={setAutoRefresh}
            intervalMs={REFRESH_INTERVAL_MS.flowGraph}
            updatedAt={resource.updatedAt}
            failing={resource.error !== null}
          />
          <button type="button" className="button" onClick={resource.reload}>
            Actualizar
          </button>
        </div>
      </header>

      <ErrorBanner error={resource.error} onRetry={resource.reload} />

      {graph === null ? null : <GraphSummary graph={graph} reference={reference} />}

      {/* Va sobre el grafo: saltar a la corrida de ayer es lo primero que se busca. */}
      {id === '' ? null : <RunTimeline correlationId={id} />}

      <section className="panel">
        <header className="panel__header">
          <h2>Pasos ({graph?.nodes.length ?? 0})</h2>
          <FlowGraphLegend />
        </header>
        <div className="panel__body">
          {resource.loading && graph === null ? (
            <LoadingBlock />
          ) : graph === null || graph.nodes.length === 0 ? (
            <EmptyState
              title="No hay mensajes con este correlation id."
              hint="Puede que el hilo viaje con otra referencia externa (Altanet y SAP la llevan como texto)."
            />
          ) : (
            <FlowGraphCanvas
              graph={graph}
              selectedNodeId={selected?.id ?? null}
              onSelectStep={onSelectStep}
              height={640}
            />
          )}
        </div>
      </section>

      {selectedStep === null ? null : (
        <FlowStepDetail step={selectedStep} onClose={() => setSelected(null)} />
      )}

      {graph === null || graph.related.length === 0 ? null : <RelatedRuns graph={graph} />}

      {/*
        Se dibuja en un portal a la derecha y, en pantallas anchas, la página le
        reserva su ancho: el grafo se redimensiona a su lado en vez de quedar
        tapado (ver styles/side-panel.css). Va al final para no remontar a sus
        hermanos: el grafo sigue montado con el panel abierto.
      */}
      <ProcessReferencePanel state={reference} />
    </div>
  );
}

/** Cabecera con el resumen de la corrida y el acceso a la guía de su flujo. */
function GraphSummary({
  graph,
  reference,
}: {
  graph: FlowGraph;
  reference: ProcessReferencePanelState;
}): ReactElement {
  // Los flujos de otros pasos también se nombran: quien llegó pinchando "F13" en
  // el listado tiene que ver F13 acá, aunque la corrida se informe como de F12.
  const otherFlows = flowsOfRun(graph).filter((flow) => flow !== graph.flow);
  return (
    <section className="panel">
      <div className="panel__body row" style={{ gap: 'var(--gap, 1rem)', flexWrap: 'wrap' }}>
        <span>
          <strong>{flowLabel(graph.flow)}</strong>
        </span>
        {otherFlows.map((flow) => (
          <span key={flow}>
            <span className="muted">y </span>
            <strong>{flowLabel(flow)}</strong>
          </span>
        ))}
        <span className="muted">{graph.messageCount} mensajes</span>
        <span className="muted">
          {graph.durationMs === null ? 'sin duración' : `${(graph.durationMs / 1000).toFixed(1)} s`}
        </span>
        <span className="muted">
          {graph.startedAt === null ? '—' : formatDateTime(graph.startedAt)}
        </span>
        <ProcessReferenceToggle state={reference} className="process-reference-toggle--end">
          Referencia del flujo
        </ProcessReferenceToggle>
      </div>
    </section>
  );
}

/** Corridas que gatillaron a esta, o que esta gatillo. */
function RelatedRuns({ graph }: { graph: FlowGraph }): ReactElement {
  return (
    <section className="panel">
      <header className="panel__header">
        <h2>Corridas relacionadas ({graph.related.length})</h2>
      </header>
      <div className="panel__body">
        <p className="muted">
          Unidas a esta por causalidad registrada: un mensaje de una corrida originó al de la otra.
        </p>
        <ul>
          {graph.related.map((relation) => (
            <li key={`${relation.reason}:${relation.correlationId}`}>
              {relation.reason === 'parent' ? 'La gatilló ' : 'Gatilló a '}
              <Link to={`/flujo/${relation.correlationId}`}>
                <span className="mono">{relation.correlationId.slice(0, 8)}</span>
              </Link>{' '}
              <span className="muted">
                {flowLabel(relation.flow)} · {relation.messageCount} mensajes
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
